// components/create/InvitationPreview.js
//
// The live preview: the published invitation page in an iframe
// (pages/InvitationPreviewFrame.js), fed the unsaved invitation on every edit.
//
// Two ways it is shown, both from CreateEvent:
//   - InvitationPreview        the bare frame. On desktop it sits in a phone
//                              outline beside the form and follows every edit.
//   - InvitationPreviewSheet   full screen over the form, from the header's
//                              Preview button. How a phone sees it.
//
// Effects play only in here on desktop, not behind the form - see
// CreateEvent.js.
import React, { useEffect, useRef, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import Portal from '../Portal';
import { PREVIEW_READY, PREVIEW_EVENT } from '../../utils/previewEvent';
import '../../styles/InvitationPreview.scss';

export default function InvitationPreview({ row, className = '' }) {
  const frameRef = useRef(null);
  const rowRef = useRef(row);
  rowRef.current = row;

  const send = useCallback(() => {
    const win = frameRef.current?.contentWindow;
    if (win) win.postMessage({ type: PREVIEW_EVENT, row: rowRef.current }, window.location.origin);
  }, []);

  // The frame asks once it is listening; until then a post would be lost.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.source !== frameRef.current?.contentWindow) return;
      if (e.data?.type === PREVIEW_READY) send();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [send]);

  // Every edit, lightly debounced so typing a title is not a re-render per key.
  useEffect(() => {
    const t = setTimeout(send, 120);
    return () => clearTimeout(t);
  }, [row, send]);

  return (
    <iframe
      ref={frameRef}
      src="/invitation-preview"
      title="Invitation preview"
      className={`invitation-preview-frame ${className}`}
    />
  );
}

export function InvitationPreviewSheet({ row, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <Portal>
      <div className="invitation-preview-sheet" role="dialog" aria-modal="true" aria-label="Invitation preview">
        <div className="invitation-preview-sheet-bar">
          <span>Preview</span>
          <small>How guests will see it</small>
          <button type="button" onClick={onClose} aria-label="Close preview"><FiX /></button>
        </div>
        <InvitationPreview row={row} className="is-sheet" />
      </div>
    </Portal>
  );
}
